import React, { useEffect, useState, createContext, useContext, memo } from 'react';
import { supabase } from '../utils/supabase';
import { User, UserRole, ModulePermissions, getRolePermissions, normalizeRoleName } from '../types';
import { hashPassword, verifyPassword, isPasswordHashed, checkRateLimit, recordFailedAttempt, resetRateLimit, createSession, getValidSession, destroySession, extendSession, logSecurityEvent } from '../utils/security';
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  sessionWarning: boolean;
  sessionSecondsLeft: number;
  extendCurrentSession: () => void;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  register: (name: string, email: string, password: string, role?: UserRole) => Promise<{
    success: boolean;
    error?: string;
  }>;
  logout: () => void;
  permissions: ModulePermissions | null;
  updateUserPreferences: (language: string, timezone: string) => void;
  updateUser: (updates: Partial<User>) => void;
  users: User[];
  updateUsers: (users: User[]) => void;
  isConnectedToSupabase: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Normalize role from DB format to app format
const normalizeRole = (role: string): UserRole => {
  return normalizeRoleName(role) as UserRole;
};
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [isConnectedToSupabase, setIsConnectedToSupabase] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(0);
  // ==================== INACTIVITY TIMEOUT ====================
  const WARNING_BEFORE_MS = 2 * 60 * 1000; // Show warning 2 min before expiry
  const extendCurrentSession = () => {
    extendSession();
    setSessionWarning(false);
    setSessionSecondsLeft(0);
  };
  // Activity listeners - extend session on any user interaction
  useEffect(() => {
    if (!user) return;
    let debounceTimer: ReturnType<typeof setTimeout>;
    const handleActivity = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Only extend if no warning is showing (user is actively working)
        if (!sessionWarning) {
          extendSession();
        }
      }, 500);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, handleActivity, {
      passive: true
    }));
    return () => {
      clearTimeout(debounceTimer);
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [user, sessionWarning]);
  // ==================== SESSION EXPIRATION MONITOR ====================
  // Check session validity every 10 seconds; show warning before expiry
  useEffect(() => {
    if (!user) return;
    const checkSession = () => {
      const session = getValidSession();
      if (!session) {
        console.log('⏰ Sesión expirada - cerrando sesión automáticamente');
        logSecurityEvent('session_expired', user?.email);
        setSessionWarning(false);
        setSessionExpired(true);
        setUser(null);
        setUsers([]);
        return;
      }
      const now = Date.now();
      const msLeft = session.expiresAt - now;
      if (msLeft <= WARNING_BEFORE_MS) {
        const secondsLeft = Math.ceil(msLeft / 1000);
        setSessionSecondsLeft(secondsLeft);
        setSessionWarning(true);
      } else {
        setSessionWarning(false);
        setSessionSecondsLeft(0);
      }
    };
    // Check every 10 seconds for accurate countdown
    const interval = setInterval(checkSession, 10 * 1000);
    // Also check on window focus (user returns to tab)
    const handleFocus = () => checkSession();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);
  // Initialize - check Supabase connection and restore session
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('id').limit(1);
        if (!error) {
          setIsConnectedToSupabase(true);
          // Try to restore session - check new secure session first, then legacy
          const session = getValidSession();
          const sessionUserId = session?.userId || localStorage.getItem('savidna-user-id');
          if (sessionUserId) {
            const {
              data: userData,
              error: userError
            } = await supabase.from('users').select('*').eq('id', sessionUserId).single();
            if (userData && !userError) {
              setUser({
                id: userData.id,
                email: userData.email,
                password: '',
                name: userData.name,
                role: normalizeRole(userData.role),
                gender: userData.gender || undefined,
                avatar: userData.avatar,
                language: userData.language || 'es',
                timezone: userData.timezone || 'America/Santo_Domingo',
                createdAt: new Date(userData.created_at)
              });
              // Migrate legacy session to new secure session
              if (!session && localStorage.getItem('savidna-user-id')) {
                createSession(userData.id);
                localStorage.removeItem('savidna-user-id');
              }
            } else {
              destroySession();
              localStorage.removeItem('savidna-user-id');
            }
          }
        } else {
          setIsConnectedToSupabase(false);
        }
      } catch (err) {
        setIsConnectedToSupabase(false);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);
  // Load all users when admin logs in (without exposing passwords)
  useEffect(() => {
    const loadAllUsers = async () => {
      if (user?.role === 'administrador' && isConnectedToSupabase) {
        const {
          data,
          error
        } = await supabase.from('users').select('id, email, name, role, avatar, language, timezone, created_at').order('created_at', {
          ascending: false
        });
        if (data && !error) {
          setUsers(data.map(u => ({
            id: u.id,
            email: u.email,
            password: '',
            name: u.name,
            role: normalizeRole(u.role),
            gender: u.gender || undefined,
            avatar: u.avatar,
            language: u.language || 'es',
            timezone: u.timezone || 'America/Santo_Domingo',
            createdAt: new Date(u.created_at)
          })));
        }
      }
    };
    if (user) loadAllUsers();
  }, [user, isConnectedToSupabase]);
  const login = async (email: string, password: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    // Clear any previous session expired state on new login attempt
    setSessionExpired(false);
    if (!isConnectedToSupabase) {
      return {
        success: false,
        error: 'No hay conexión con Supabase. Verifica tu configuración.'
      };
    }
    // Check rate limit before attempting login
    const rateCheck = checkRateLimit();
    if (!rateCheck.allowed) {
      logSecurityEvent('rate_limited', email);
      return {
        success: false,
        error: rateCheck.message
      };
    }
    try {
      const {
        data,
        error
      } = await supabase.from('users').select('*').eq('email', email.toLowerCase().trim()).single();
      if (error || !data) {
        const result = recordFailedAttempt();
        logSecurityEvent('login_failed', `Usuario no encontrado: ${email}`);
        return {
          success: false,
          error: result.message || 'Credenciales incorrectas'
        };
      }
      // Verify password - support both hashed and legacy plaintext
      let passwordValid = false;
      try {
        if (isPasswordHashed(data.password)) {
          passwordValid = await verifyPassword(password, data.password);
        } else {
          // Legacy plaintext comparison
          passwordValid = data.password === password;
          if (passwordValid) {
            // Auto-migrate: hash the plaintext password in background
            try {
              const hashedPw = await hashPassword(password);
              await supabase.from('users').update({
                password: hashedPw
              }).eq('id', data.id);
              console.log('✅ Contraseña migrada a hash seguro');
            } catch (migrationErr) {
              // Migration failed but login still succeeds
              console.warn('⚠️ No se pudo migrar la contraseña a hash:', migrationErr);
            }
          }
        }
      } catch (hashErr) {
        // If hashing fails (e.g., crypto API issue), fall back to plaintext comparison
        console.warn('⚠️ Error en verificación de hash, usando comparación directa:', hashErr);
        passwordValid = data.password === password;
      }
      if (!passwordValid) {
        const result = recordFailedAttempt();
        logSecurityEvent('login_failed', `Contraseña incorrecta: ${email}`);
        return {
          success: false,
          error: result.message || 'Credenciales incorrectas'
        };
      }
      // Success - create secure session
      const loggedUser: User = {
        id: data.id,
        email: data.email,
        password: '',
        name: data.name,
        role: normalizeRole(data.role),
        gender: data.gender || undefined,
        avatar: data.avatar,
        language: data.language || 'es',
        timezone: data.timezone || 'America/Santo_Domingo',
        createdAt: new Date(data.created_at)
      };
      setUser(loggedUser);
      createSession(data.id);
      resetRateLimit();
      logSecurityEvent('login_success', data.email);
      return {
        success: true
      };
    } catch (err: any) {
      console.error('❌ Error en login:', err);
      return {
        success: false,
        error: 'Error al iniciar sesión: ' + (err?.message || 'Error desconocido')
      };
    }
  };
  const register = async (name: string, email: string, password: string, role: UserRole = 'recepcionista'): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!isConnectedToSupabase) {
      return {
        success: false,
        error: 'No hay conexión con Supabase'
      };
    }
    try {
      // Check if email already exists
      const {
        data: existing
      } = await supabase.from('users').select('id').eq('email', email.toLowerCase().trim()).single();
      if (existing) {
        return {
          success: false,
          error: 'El correo ya está registrado'
        };
      }
      // Hash password before storing
      const hashedPassword = await hashPassword(password);
      // Force role to recepcionista for public registration (security)
      const safeRole = 'recepcionista';
      const {
        data,
        error
      } = await supabase.from('users').insert({
        id: crypto.randomUUID(),
        email: email.toLowerCase().trim(),
        password: hashedPassword,
        name: name.trim(),
        role: safeRole,
        language: 'es',
        timezone: 'America/Santo_Domingo'
      }).select().single();
      if (error) {
        return {
          success: false,
          error: 'Error al registrar. Intenta de nuevo.'
        };
      }
      const newUser: User = {
        id: data.id,
        email: data.email,
        password: '',
        name: data.name,
        role: normalizeRole(data.role),
        gender: data.gender || undefined,
        language: 'es',
        timezone: 'America/Santo_Domingo',
        createdAt: new Date(data.created_at)
      };
      setUser(newUser);
      createSession(data.id);
      logSecurityEvent('login_success', `Nuevo registro: ${data.email}`);
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: 'Error al registrar. Intenta de nuevo.'
      };
    }
  };
  const logout = () => {
    logSecurityEvent('logout', user?.email);
    destroySession();
    setSessionExpired(false);
    setSessionWarning(false);
    setSessionSecondsLeft(0);
    setUser(null);
    setUsers([]);
  };
  const updateUserPreferences = async (language: string, timezone: string) => {
    if (!user) return;
    if (isConnectedToSupabase) {
      await supabase.from('users').update({
        language,
        timezone
      }).eq('id', user.id);
    }
    setUser({
      ...user,
      language,
      timezone
    });
  };
  const updateUser = async (updates: Partial<User>) => {
    if (!user) return;
    const dbUpdates: Record<string, any> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.role !== undefined) dbUpdates.role = updates.role;
    if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
    if (updates.avatar !== undefined) dbUpdates.avatar = updates.avatar;
    if (updates.language !== undefined) dbUpdates.language = updates.language;
    if (updates.timezone !== undefined) dbUpdates.timezone = updates.timezone;
    // Hash password if being updated
    if (updates.password) {
      dbUpdates.password = await hashPassword(updates.password);
      logSecurityEvent('password_changed', user.email);
    }
    if (isConnectedToSupabase && Object.keys(dbUpdates).length > 0) {
      await supabase.from('users').update(dbUpdates).eq('id', user.id);
    }
    const updatedUser = {
      ...user,
      ...updates,
      password: '' // Never keep password in memory
    };
    setUser(updatedUser);
    // Also sync the users array so UserManagement stays up to date
    if (users.length > 0) {
      setUsers(prevUsers => prevUsers.map(u => u.id === user.id ? {
        ...u,
        ...updatedUser
      } : u));
    }
  };
  const updateUsers = async (updatedUsers: User[]) => {
    if (isConnectedToSupabase) {
      for (const u of updatedUsers) {
        const dbUpdates: Record<string, any> = {
          name: u.name,
          role: u.role,
          avatar: u.avatar,
          language: u.language,
          timezone: u.timezone
        };
        // Only update password if it's provided and not empty
        if (u.password && u.password.trim() !== '') {
          dbUpdates.password = await hashPassword(u.password);
        }
        await supabase.from('users').update(dbUpdates).eq('id', u.id);
      }
    }
    // Clear passwords from memory
    const safeUsers = updatedUsers.map(u => ({
      ...u,
      password: ''
    }));
    setUsers(safeUsers);
    if (user) {
      const current = safeUsers.find(u => u.id === user.id);
      if (current) setUser(current);
    }
  };
  const permissions = user ? getRolePermissions(user.role) : null;
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionExpired,
    sessionWarning,
    sessionSecondsLeft,
    extendCurrentSession,
    login,
    register,
    logout,
    permissions,
    updateUserPreferences,
    updateUser,
    users,
    updateUsers,
    isConnectedToSupabase
  }} data-id="element-4019">
      {children}
    </AuthContext.Provider>;
}
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}